
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/sonner';
import { DayOfWeek } from '@/models/Schedule';
import { TimeSlotFormData } from '../timeSlotFormSchema';
import { useTimeSlotValidation } from '../useTimeSlotValidation';
import { 
  generateRecurringTimeSlots, 
  validateRecurringSettings 
} from '../RecurringTimeSlotsGenerator';
import { createRegularTimeSlots, validateOverlaps } from '../utils/timeSlotFormUtils';

interface UseTimeSlotSubmissionProps {
  existingTimeSlots: any[];
  onCreateTimeSlots: (timeSlots: any[]) => Promise<void>;
  onOpenChange: (open: boolean) => void;
}

export const useTimeSlotSubmission = ({
  existingTimeSlots,
  onCreateTimeSlots,
  onOpenChange,
}: UseTimeSlotSubmissionProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { validateTimeRange, checkForOverlaps } = useTimeSlotValidation(existingTimeSlots);

  const handleRecurringSubmission = async (data: TimeSlotFormData) => {
    console.log('Processing recurring time slots...');
    
    // Validate recurring settings
    const validation = validateRecurringSettings(
      data.recurringStartTime!,
      data.recurringEndTime!,
      data.recurringInterval!,
      data.slotDuration!
    );

    if (!validation.isValid) {
      console.log('Recurring validation failed:', validation.error);
      toast.error(validation.error || t('schedule.invalidRecurringSettings'));
      return null;
    }

    console.log('Recurring settings are valid, generating slots...');

    // Generate recurring time slots
    let newTimeSlots = generateRecurringTimeSlots({
      selectedDays: data.selectedDays,
      recurringStartTime: data.recurringStartTime!,
      recurringEndTime: data.recurringEndTime!,
      recurringInterval: data.recurringInterval!,
      slotDuration: data.slotDuration!,
      workoutTypeId: data.workoutTypeId,
      maxClients: data.maxClients,
    });

    console.log('Generated recurring time slots:', newTimeSlots);

    // Check for overlaps with existing slots
    const overlappingSlots = newTimeSlots.filter(slot => 
      checkForOverlaps(
        slot.day_of_week as DayOfWeek,
        slot.start_time,
        slot.end_time,
        slot.workout_type_id
      )
    );

    if (overlappingSlots.length > 0) {
      console.log('Some recurring slots overlap, skipping them');
      newTimeSlots = newTimeSlots.filter(slot => 
        !checkForOverlaps(
          slot.day_of_week as DayOfWeek,
          slot.start_time,
          slot.end_time,
          slot.workout_type_id
        )
      );
      
      if (newTimeSlots.length === 0) {
        toast.error(t('schedule.allSlotsOverlap'));
        return null;
      }
      
      toast.warning(t('schedule.someOverlapSkipped'));
    }

    return newTimeSlots;
  };

  const handleRegularSubmission = async (data: TimeSlotFormData) => {
    console.log('Processing regular time slots...');
    
    // Regular single time slot creation
    if (!validateTimeRange(data.startTime!, data.endTime!)) {
      console.log('Time range validation failed');
      toast.error(t('schedule.invalidTimeRange'));
      return null;
    }

    // Check for overlaps with the same workout type
    const overlappingDays = validateOverlaps(data, checkForOverlaps);

    if (overlappingDays.length > 0) {
      console.log('Overlap detected for days:', overlappingDays);
      toast.error(t('schedule.overlapError'));
      return null;
    }

    // Create time slots data for the parent component to handle
    return createRegularTimeSlots(data);
  };

  const onSubmit = async (data: TimeSlotFormData) => {
    console.log('Form submission started with data:', data);
    console.log('Is recurring mode:', data.isRecurringMode);
    setIsSubmitting(true);

    try {
      let newTimeSlots: any[] = [];

      if (data.isRecurringMode) {
        const recurringSlots = await handleRecurringSubmission(data);
        if (!recurringSlots) {
          setIsSubmitting(false);
          return;
        }
        newTimeSlots = recurringSlots;
      } else {
        const regularSlots = await handleRegularSubmission(data);
        if (!regularSlots) {
          setIsSubmitting(false);
          return;
        }
        newTimeSlots = regularSlots;
      }

      console.log('Creating time slots:', newTimeSlots);
      await onCreateTimeSlots(newTimeSlots);
      console.log('Time slots created successfully');
      
      toast.success(
        data.isRecurringMode 
          ? t('schedule.recurringCreated', { count: newTimeSlots.length })
          : t('schedule.createSuccess')
      );
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error in form submission:', error);
      toast.error(t('schedule.errors.createFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    onSubmit,
  };
};
